/**
 * Cheerios.js - Buzztime API Service
 * Handles communication with the Buzztime API for spectator mode
 */

import axios from 'axios';
import CryptoJS from 'crypto-js';
import {systemInfo} from "@/systemInfo.js";
import {remoteAPI} from "@/services/axios.js";
import { AppSchema } from '@/schemas';

// Variables Import
const API_BASE_URL = import.meta.env.VITE_CLOUDHUB_BASE_URL;
const API_V2_URL = import.meta.env.VITE_CHEERIOS_V2_URL;
const REMOTE_API_URL = import.meta.env.VITE_REMOTE_API_URL;
const HANDSHAKE_JWT_KEY = import.meta.env.VITE_HANDSHAKE_JWT_KEY;
const PW_URL = import.meta.env.VITE_PW_URL
const PW_AUTH_HEADER = import.meta.env.VITE_PW_AUTH_HEADER

function getServicesUrl(endpoint) {
    return API_BASE_URL + endpoint;
}

function getServiceUrlV2(endpoint) {
    return API_V2_URL + endpoint
}

function getRemoteUrl(endpoint) {
    return REMOTE_API_URL + endpoint
}

function getBase64Encoded(raw) {
    const wordArray = CryptoJS.enc.Utf8.parse(raw);
    return CryptoJS.enc.Base64.stringify(wordArray);
}

function createJWT(header, payload, secret) {
    const header64 = getBase64Encoded(header);
    const payload64 = getBase64Encoded(payload);
    const signature = CryptoJS.HmacSHA256(header64 + "." + payload64, secret);
    const signature64 = CryptoJS.enc.Base64.stringify(signature);

    return header64 + "." + payload64 + "." + signature64;
}

function createHeaders(keySrc) {
    const expiration = Math.floor(Date.now() / 1000) + (60 * 5);
    const header = '{ "alg":"HS256", "typ":"JWT" }';
    const payload = `{ "exp": ${expiration} }`;
    const jwt = createJWT(header, payload, keySrc);

    return {'Authorization': jwt, 'Content-Type': 'application/json'}
}


export async function getAppsForSite(siteId) {
    try {
        console.log(`Fetching apps from Remote for ${siteId}`)

        const endpoint = `/remote/games/?site_id=${siteId}`
        const url = getRemoteUrl(endpoint)

        const response = await axios.get(url)

        console.log('Apps fetch successful:');

        return response.data;
    } catch (error) {
        console.error('Apps Fetch error:', error);

        // Check if it's an Axios error with a response
        if (error.response) {
            console.error('Error response:', error.response.data);
            throw new Error(`Apps fetch failed: ${error.response.status} - ${JSON.stringify(error.response.data)}`);
        }

        // Network errors or other issues
        throw new Error(`Apps fetch failed: ${error.message}`);
    }
}

export async function getAppsForDevice() {
    try {
        const accessToken = localStorage.getItem('remote_auth_token');

        if (!accessToken) {
            throw new Error('No access token found');
        }
        
        const appsResposne = await remoteAPI.get(`/devices/me/games/?token=${accessToken}`);
        const appsData = AppSchema.array().parse(appsResposne.data);

        console.log('Apps fetch successful:', appsData);
        return appsData;
    } catch (error) {
        console.error('Apps Fetch error:', error);
        throw new Error(`Apps fetch failed: ${error.message}`);
    }
}

export async function getDefaultAppForTv(tvId, siteId) {
    try {
        console.log(`Fetching default app from Remote for tv ${tvId} - ${siteId}`)

        const endpoint = `/tvs/public?tv_id=${tvId}&site_id=${siteId}`
        const url = getRemoteUrl(endpoint)

        const response = await axios.get(url)

        if (response.status !== 200) {
            throw new Error(`Default app fetch failed with status: ${response.status}`);
        }

        console.log('Default app fetch successful:');

        return response.data;
    } catch (error) {
        console.error('Default app Fetch error:', error);

        // Check if it's an Axios error with a response
        if (error.response) {
            console.error('Error response:', error.response.data);
            throw new Error(`Default app fetch failed: ${error.response.status} - ${JSON.stringify(error.response.data)}`);
        }

        // Network errors or other issues
        throw new Error(`Default app fetch failed: ${error.message}`);
    }
}

/**
 * Perform a handshake with the Buzztime server
 * @param {string} serialNumber - Device serial number
 * @returns {Promise<Object>} - Handshake response data
 */
export async function spectatorHandshake(serialNumber) {
    try {
        console.log(`Performing handshake for device: ${serialNumber}`);

        const endpoint = '/cloudhub/spectator/handshake/';
        const url = getServicesUrl(endpoint);
        const headers = createHeaders(HANDSHAKE_JWT_KEY)

        const body = {
            serial_number: serialNumber,
            app_version: import.meta.env.VITE_APP_VERSION,
            device_type: 'kiosk',
            platform: 'web'
        };

        const response = await axios.post(url, body, {headers});

        if (response.status !== 200) {
            throw new Error(`Handshake failed with status: ${response.status}`);
        }

        console.log('Handshake successful:');
        return response.data;
    } catch (error) {
        console.error('Handshake error:', error);

        // Check if it's an Axios error with a response
        if (error.response) {
            console.error('Error response:', error.response.data);
            throw new Error(`Handshake failed: ${error.response.status} - ${JSON.stringify(error.response.data)}`);
        }

        // Network errors or other issues
        throw new Error(`Handshake failed: ${error.message}`);
    }
}

/**
 * Get Bar Hero status for site.
 * Sample response:
 * {
 *   "site_id": 42768,
 *   "site_name": "buzzteest",
 *   "merchant_id": "SPOT38935",
 *   "active": true,
 *   "is_trial": false,
 *   "wheel_url": "https://wheel.dxn.live/GAME-SPOT38935"
 * }
 * @param siteId
 * @returns {Promise<*>}
 */
export async function barHero_getSiteStatus(siteId) {
    try {
        console.log(`Getting Bar Hero status for site ${siteId}`);

        const endpoint = `/bmo/sites/${siteId}`
        const url = getServiceUrlV2(endpoint)
        const headers = {
            'Authorization': PW_AUTH_HEADER,
            'Content-Type': 'application/json'
        }

        const response = await axios.get(url, {headers});

        if (response.status !== 200) {
            throw new Error(`Bar Hero status failed with status: ${response.status}`);
        }

        return response.data;
    } catch (error) {

        if (error.response) {
            console.error(`Bar Hero status failed: ${error.response.status} - ${JSON.stringify(error.response.data)}`);
        } else {
            console.error(`Bar Hero status failed: ${error.message}`);
        }

        return {active: false}
    }
}

// Export additional utility functions as needed
export default {
    spectatorHandshake,
    barHero_getSiteStatus,
    getAppsForSite
    // Connect_mqtt_cloudhub,
    // getBroadcastInProgress,
    // logSchedule,
    // setBroadcastId
};
